/**
 * Accounting (Phase 2): chart of accounts, posting accounts, vouchers with lines and
 * allocations, post-dated cheques, bank reconciliation and report types.
 */
import { z } from 'zod';
import { always } from './schemas';

const req = (label: string, max: number) => z.string().trim().min(1, `${label} is required`).max(max, `${label} must be at most ${max} characters`);
const opt = (max: number) => z.string().trim().max(max, `At most ${max} characters`).optional().default('');
const isoDate = z.string({ error: 'Enter a date' }).regex(/^\d{4}-\d{2}-\d{2}$/, 'Enter a valid date');
const uuidOpt = z.string().uuid().nullable().optional().transform((v) => v ?? null);
/** Money: up to 2 decimals, non-negative. '' → 0. */
const money = (label = 'Amount') =>
  z.preprocess(
    (v) => (v === '' || v == null ? 0 : v),
    z.coerce
      .number({ error: `Enter a valid ${label.toLowerCase()}` })
      .min(0, `${label} cannot be negative`)
      .max(1e11, `${label} is too large`)
      .refine((n) => Math.round(n * 100) === Math.round(n * 1000) / 10, `${label} has at most 2 decimals`),
  );
export const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

// ---------- Chart of accounts ----------

export const ACCOUNT_TYPES = ['ASSET', 'LIABILITY', 'EQUITY', 'INCOME', 'EXPENSE'] as const;
export type AccountType = (typeof ACCOUNT_TYPES)[number];
export const ACCOUNT_TYPE_LABELS: Record<AccountType, string> = { ASSET: 'Assets', LIABILITY: 'Liabilities', EQUITY: 'Equity', INCOME: 'Revenue', EXPENSE: 'Expenses' };
/** Normal balance side: assets and expenses are debit balances. */
export const DEBIT_NATURE: Record<AccountType, boolean> = { ASSET: true, EXPENSE: true, LIABILITY: false, EQUITY: false, INCOME: false };

export const ACCOUNT_KINDS = ['GENERAL', 'BANK', 'CASH', 'RECEIVABLE', 'PAYABLE', 'PDC_RECEIVED', 'PDC_ISSUED', 'PDC_CLEARING', 'VAT_INPUT', 'VAT_OUTPUT'] as const;
export type AccountKind = (typeof ACCOUNT_KINDS)[number];
export const ACCOUNT_KIND_LABELS: Record<AccountKind, string> = {
  GENERAL: 'General',
  BANK: 'Bank',
  CASH: 'Cash',
  RECEIVABLE: 'Receivable (customers)',
  PAYABLE: 'Payable (suppliers)',
  PDC_RECEIVED: 'PDC received',
  PDC_ISSUED: 'PDC issued',
  PDC_CLEARING: 'PDC clearing (legacy)',
  VAT_INPUT: 'VAT input',
  VAT_OUTPUT: 'VAT output',
};
/** Kinds whose lines must name a customer/supplier (sub-ledger). */
export const PARTY_KINDS: readonly AccountKind[] = ['RECEIVABLE', 'PAYABLE'];
export const CASH_KINDS: readonly AccountKind[] = ['BANK', 'CASH'];

/** Levels: 1 class (1 digit), 2 group (3 digits), 3 head (6 digits), 4 account (11 digits, postable). */
export const ACCOUNT_CODE_LENGTH = [0, 1, 3, 6, 11] as const;

/** Fixed groups (level 2 is set by the system, so these codes never change). */
export const CURRENT_ASSETS_GROUP = '102';
export const CURRENT_LIABILITIES_GROUP = '202';

/**
 * Which kinds a new account may have under a head (level 3), given the kinds already under it.
 * Bank and cash: only under a Current Assets head that holds bank / cash accounts (or a new, empty one).
 * VAT: under a Current Assets / Current Liabilities head that holds VAT accounts (or a new, empty one). Customers and suppliers are never opened here: they are
 * added in Sales › Customers and Inventory › Suppliers, posting to the existing control accounts.
 */
export function accountKindsUnder(headCode: string, childKinds: readonly AccountKind[]): AccountKind[] {
  const kinds: AccountKind[] = ['GENERAL'];
  const current = headCode.startsWith(CURRENT_ASSETS_GROUP);
  if (current && (!childKinds.length || childKinds.some((k) => k === 'BANK' || k === 'CASH'))) kinds.push('BANK', 'CASH');
  const vatHead = !childKinds.length || childKinds.some((k) => k === 'VAT_INPUT' || k === 'VAT_OUTPUT');
  if ((current || headCode.startsWith(CURRENT_LIABILITIES_GROUP)) && vatHead) kinds.push('VAT_INPUT', 'VAT_OUTPUT');
  return kinds;
}

/** Why a kind is not offered under a head (the message the server gives). */
export function accountKindRefusal(kind: AccountKind): string {
  if (kind === 'RECEIVABLE') return 'Customers are added in Sales › Customers, not as accounts in the chart';
  if (kind === 'PAYABLE') return 'Suppliers are added in Inventory › Suppliers, not as accounts in the chart';
  if (kind === 'BANK' || kind === 'CASH') return 'Bank and cash accounts go under a Current Assets head that holds bank and cash accounts';
  if (kind === 'VAT_INPUT' || kind === 'VAT_OUTPUT') return 'VAT accounts go under the VAT head (Current Assets or Current Liabilities)';
  return 'This kind of account is set up by the system';
}

/** New suppliers without history post to their group's head (its supplier control account). */
export const SUPPLIER_GROUP_HEAD: Partial<Record<'TRADE_CASH' | 'TRADE_CREDIT' | 'SERVICE' | 'OTHER', string>> = { TRADE_CASH: '202001', TRADE_CREDIT: '202002', SERVICE: '202014', OTHER: '202015' };

/** Account names are compared ignoring case and spacing: "Cash in Hand" = "cash  in hand". */
export const tidyAccountName = (s: string) => s.trim().replace(/\s+/g, ' ');
export const accountNameKey = (s: string) => tidyAccountName(s).toLowerCase();

/**
 * Levels 1 and 2 are fixed; users add heads (level 3) under a group and accounts (level 4) under a head.
 * The code is never typed: the server gives the next number in the parent's sequence.
 */
export const accountSchema = z.object({
  name: req('Account name', 160).transform(tidyAccountName),
  // Required for heads and accounts; classes (level 1) have none.
  parentId: uuidOpt,
  kind: z.enum(ACCOUNT_KINDS).default('GENERAL'),
  requiresCostCentre: z.boolean().default(false),
  isActive: z.boolean(),
});
export type AccountInput = z.infer<typeof accountSchema>;

export interface AccountNode {
  id: string;
  code: string;
  name: string;
  level: number;
  parentId: string | null;
  type: AccountType;
  kind: AccountKind;
  isPostable: boolean;
  requiresParty: boolean;
  requiresCostCentre: boolean;
  isActive: boolean;
  legacyType: string | null;
  version: number;
  /** Closing balance (debit positive) as of today. */
  balance: number;
  /** Posted ledger lines on this account and everything under it. */
  entries: number;
  /** Used as a posting account or by a bank record (cannot be deleted). */
  configured?: boolean;
  /** Postable accounts under a group / head. */
  accounts: number;
  /** Customers / suppliers with entries on a control account. */
  parties: number;
}

/** Account picker entry. */
export interface AccountRef {
  id: string;
  code: string;
  name: string;
  type: AccountType;
  kind: AccountKind;
  requiresParty: boolean;
  requiresCostCentre: boolean;
  isActive: boolean;
}

// ---------- Posting accounts (configurable, no hard-coded codes) ----------

export const POSTING_KEYS = {
  'ar.control': { label: 'Customers (receivables control)', kinds: ['RECEIVABLE'] },
  'ap.control': { label: 'Suppliers (payables control)', kinds: ['PAYABLE'] },
  'pdc.received': { label: 'Post-dated cheques received', kinds: ['PDC_RECEIVED'] },
  'pdc.issued': { label: 'Post-dated cheques issued', kinds: ['PDC_ISSUED'] },
  'vat.output': { label: 'VAT output (sales)', kinds: ['VAT_OUTPUT'] },
  'vat.input': { label: 'VAT input (purchases)', kinds: ['VAT_INPUT'] },
  'discount.allowed': { label: 'Discount allowed to customers', kinds: ['GENERAL'] },
  'discount.received': { label: 'Discount received from suppliers', kinds: ['GENERAL'] },
  'depreciation.expense': { label: 'Depreciation expense (P&L)', kinds: ['GENERAL'] },
  'asset.disposal': { label: 'Gain / loss on disposal of fixed assets (P&L)', kinds: ['GENERAL'] },
  'sales.revenue': { label: 'Sales of goods', kinds: ['GENERAL'] },
  'sales.services': { label: 'Sales of services (service items)', kinds: ['GENERAL'] },
  'sales.returns': { label: 'Sales returns and allowances (credit notes)', kinds: ['GENERAL'] },
  cogs: { label: 'Cost of goods sold (perpetual only)', kinds: ['GENERAL'] },
  purchases: { label: 'Purchases (periodic: goods bought are expensed here)', kinds: ['GENERAL'] },
  'inventory.default': { label: 'Inventory (balance sheet) when the item category has none', kinds: ['GENERAL'] },
  'inventory.change': { label: 'Change in inventory (P&L, month-end closing stock)', kinds: ['GENERAL'] },
  grni: { label: 'Goods received not invoiced (perpetual only)', kinds: ['GENERAL'] },
  'stock.adjustment': { label: 'Stock adjustments / consumption (perpetual only)', kinds: ['GENERAL'] },
  wip: { label: 'Work in progress (balance sheet, month-end)', kinds: ['GENERAL'] },
  'production.absorbed': { label: 'Production cost absorbed (P&L credit for labour & overhead charged to jobs)', kinds: ['GENERAL'] },
  'overhead.variance': { label: 'Over / under-absorbed production overhead (P&L)', kinds: ['GENERAL'] },
  'salary.expense': { label: 'Salaries & wages expense', kinds: ['GENERAL'] },
  'salary.payable': { label: 'Salaries payable', kinds: ['GENERAL'] },
  'employee.advances': { label: 'Advances to employees', kinds: ['GENERAL'] },
  'employee.loans': { label: 'Loans to employees', kinds: ['GENERAL'] },
  'staff.welfare_fund': { label: 'Staff welfare fund (fines deducted from salaries go here, UAE law)', kinds: ['GENERAL'] },
  'gratuity.provision': { label: 'End-of-service gratuity provision (liability)', kinds: ['GENERAL'] },
  'leave_salary.provision': { label: 'Leave salary provision (liability)', kinds: ['GENERAL'] },
  'bank.charges': { label: 'Bank charges (e.g. on returned cheques)', kinds: ['GENERAL'] },
  'cheque.bounce_income': { label: 'Returned-cheque charges recovered from customers (income)', kinds: ['GENERAL'] },
  'bad.debts': { label: 'Bad debts written off', kinds: ['GENERAL'] },
  'writeoff.small': { label: 'Small balances written off (leftover cents on invoices / bills)', kinds: ['GENERAL'] },
  rounding: { label: 'Rounding differences', kinds: ['GENERAL'] },
  'retained.earnings': { label: 'Retained earnings', kinds: ['GENERAL'] },
  'opening.balance': { label: 'Opening balances control', kinds: ['GENERAL'] },
} as const satisfies Record<string, { label: string; kinds: readonly AccountKind[] }>;
export type PostingKey = keyof typeof POSTING_KEYS;
/**
 * Posting accounts by where they are used; only these are listed and checked (owner, 2026-10-30: no "Other" section).
 * Keys left out belong to hidden features — Fixed Assets, Production Costing — or to nothing yet (leave salary provision).
 */
export const POSTING_GROUPS: readonly { title: string; hint: string; keys: readonly PostingKey[] }[] = [
  { title: 'Customers & suppliers', hint: 'Control accounts for receivables and payables, settlement discounts, and small balances written off', keys: ['ar.control', 'ap.control', 'discount.allowed', 'discount.received', 'writeoff.small'] },
  { title: 'Cheques & bounced cheques', hint: 'Where post-dated cheques sit until they clear; charges and write-offs on returned cheques', keys: ['pdc.received', 'pdc.issued', 'bank.charges', 'cheque.bounce_income', 'bad.debts'] },
  { title: 'VAT', hint: 'VAT charged on sales and paid on purchases', keys: ['vat.output', 'vat.input'] },
  { title: 'Sales', hint: 'Used when invoices and credit notes post', keys: ['sales.revenue', 'sales.services', 'sales.returns'] },
  { title: 'Purchases & stock', hint: 'Periodic stock: purchases are expensed, closing stock adjusts at month-end', keys: ['purchases', 'inventory.default', 'inventory.change', 'cogs', 'grni', 'stock.adjustment'] },
  { title: 'Payroll & employees', hint: 'Salary run postings, employee advances / loans, the staff welfare fund and gratuity', keys: ['salary.expense', 'salary.payable', 'employee.advances', 'employee.loans', 'staff.welfare_fund', 'gratuity.provision'] },
  { title: 'Opening balances & year-end', hint: 'Cut-over opening entry, closing the year, rounding', keys: ['opening.balance', 'retained.earnings', 'rounding'] },
];
/** May stay empty: nothing stops without them. */
export const OPTIONAL_POSTING_KEYS: readonly PostingKey[] = ['grni', 'stock.adjustment', 'cogs', 'discount.allowed', 'discount.received', 'rounding'];
/** Used only with perpetual stock: not listed while stock is periodic. */
export const PERPETUAL_POSTING_KEYS: readonly PostingKey[] = ['cogs', 'grni', 'stock.adjustment'];
/** The posting accounts in use for this stock method, in the order shown. */
export const postingKeysInUse = (valuationMode: 'PERIODIC' | 'PERPETUAL'): PostingKey[] =>
  POSTING_GROUPS.flatMap((g) => g.keys).filter((k) => valuationMode === 'PERPETUAL' || !PERPETUAL_POSTING_KEYS.includes(k));
export const updatePostingAccountsSchema = z.record(z.string(), z.string().uuid('Select an account').nullable());
export interface PostingAccountItem {
  key: PostingKey;
  label: string;
  kinds: readonly AccountKind[];
  account: AccountRef | null;
}

// ---------- Vouchers ----------

export const PAYMENT_MODES = ['CASH', 'CHEQUE', 'PDC', 'TRANSFER', 'CARD', 'ONLINE'] as const;
export type PaymentMode = (typeof PAYMENT_MODES)[number];
export const PAYMENT_MODE_LABELS: Record<PaymentMode, string> = {
  CASH: 'Cash',
  CHEQUE: 'Cheque (current-dated)',
  PDC: 'Post-dated cheque',
  TRANSFER: 'Bank transfer',
  CARD: 'Card',
  ONLINE: 'Online / wallet',
};

export const VOUCHER_KINDS = ['journal', 'receipt', 'payment', 'contra', 'pdc_received', 'pdc_issued'] as const;
export type VoucherKind = (typeof VOUCHER_KINDS)[number];
export const VOUCHER_DOC_TYPE: Record<VoucherKind, string> = {
  journal: 'acct.journal',
  receipt: 'acct.receipt',
  payment: 'acct.payment',
  contra: 'acct.contra',
  pdc_received: 'acct.pdc_received',
  pdc_issued: 'acct.pdc_issued',
};

export const journalLineSchema = z
  .object({
    accountId: z.string({ error: 'Select an account' }).uuid('Select an account'),
    partnerId: uuidOpt,
    costCentreId: uuidOpt,
    description: opt(300),
    debit: money('Debit'),
    credit: money('Credit'),
  })
  .refine((l) => (l.debit > 0) !== (l.credit > 0), { path: ['debit'], message: 'Enter either a debit or a credit', ...always });
export type JournalLineInput = z.infer<typeof journalLineSchema>;

export const allocationInputSchema = z.object({
  /** The open item (invoice/bill line or advance) being settled. */
  targetLineId: z.string().uuid(),
  amount: money('Allocated amount').refine((n) => n > 0, 'Enter an amount'),
  /** Of the amount, what is settled by a discount on this invoice / bill (owner: discount per invoice). */
  discount: z.preprocess((v) => (v === '' || v == null ? 0 : v), money('Discount')).default(0),
});
export type AllocationInput = z.infer<typeof allocationInputSchema>;

const voucherBase = {
  docDate: isoDate,
  narration: req('Narration', 2000),
  /** Formatted narration (cleaned on the server); `narration` is its plain text. */
  narrationRich: z.string().max(12000, 'The narration is too long').optional(),
  reference: opt(100),
};

/** JV and contra: free lines, debits must equal credits. */
export const journalVoucherSchema = z
  .object({ ...voucherBase, lines: z.array(journalLineSchema).min(2, 'A journal needs at least two lines').max(500) })
  .refine((v) => round2(v.lines.reduce((s, l) => s + l.debit, 0)) === round2(v.lines.reduce((s, l) => s + l.credit, 0)), {
    path: ['lines'],
    message: 'Total debits must equal total credits',
    ...always,
  });
export type JournalVoucherInput = z.infer<typeof journalVoucherSchema>;

/** One line of a receipt/payment: the account (and party) on the other side of the bank/cash account. */
export const cashLineSchema = z.object({
  accountId: z.string({ error: 'Select an account' }).uuid('Select an account'),
  partnerId: uuidOpt,
  /** Salary payments: the employee whose net pay is settled. */
  employeeId: uuidOpt,
  costCentreId: uuidOpt,
  description: opt(300),
  amount: money().refine((n) => n > 0, 'Enter an amount'),
  /** Receipts: an employee advance / loan this line pays back (updates HR & Payroll). */
  loanId: uuidOpt,
  /** Payments: an advance or loan given to the employee on this line (recovered from salary). */
  newLoan: z
    .object({
      kind: z.enum(['ADVANCE', 'LOAN']),
      /** Loans: monthly salary deduction. Advances come off the next salary in full. */
      installment: z.preprocess((v) => (v === '' || v == null ? 0 : v), money('Monthly deduction')).default(0),
      startMonth: z.string().regex(/^\d{4}-\d{2}$/, 'Choose the first month'),
    })
    .nullable()
    .optional(),
  /** Settlement discount: the customer / supplier is settled for amount + discount; the discount goes to discount allowed / received. */
  discount: z.preprocess((v) => (v === '' || v == null ? 0 : v), money('Discount')).default(0),
  allocations: z.array(allocationInputSchema).max(500).default([]),
});
export type CashLineInput = z.infer<typeof cashLineSchema>;

const cashBase = z.object({
  ...voucherBase,
  /** Bank/cash account money goes into (receipt) or out of (payment); for PDC the clearing account is used. */
  cashAccountId: uuidOpt,
  mode: z.enum(PAYMENT_MODES),
  partnerId: uuidOpt,
  chequeNo: z.string().trim().max(20).optional().default(''),
  chequeDate: z.union([z.literal(''), isoDate]).nullable().optional().transform((v) => v || null),
  /** Received cheques: the customer's bank. Issued cheques: our bank account. */
  drawerBank: opt(80),
  bankAccountId: uuidOpt,
  /** Recovery of a returned (bounced) cheque. */
  replacesChequeId: uuidOpt,
  lines: z.array(cashLineSchema).min(1, 'Add at least one line').max(1000),
});

const cashRules = <T extends z.ZodType<z.infer<typeof cashBase>>>(s: T) =>
  s
    .refine((v) => v.mode === 'PDC' || !!v.cashAccountId, { path: ['cashAccountId'], message: 'Select the bank or cash account', ...always })
    .refine((v) => !['CHEQUE', 'PDC'].includes(v.mode) || /^\d{1,20}$/.test(v.chequeNo), { path: ['chequeNo'], message: 'Enter the cheque number (digits)', ...always })
    .refine((v) => !['CHEQUE', 'PDC'].includes(v.mode) || !!v.chequeDate, { path: ['chequeDate'], message: 'Enter the cheque date', ...always })
    .refine((v) => v.mode !== 'PDC' || !v.chequeDate || v.chequeDate >= v.docDate, { path: ['chequeDate'], message: 'A post-dated cheque is dated on or after the voucher date', ...always })
    .refine(
      (v) => v.lines.every((l) => round2(l.allocations.reduce((s, a) => s + a.amount, 0)) <= round2(l.amount + (l.discount ?? 0))),
      { path: ['lines'], message: 'Allocations on a line cannot exceed its amount plus discount', ...always },
    )
    .refine(
      (v) => v.lines.every((l) => round2(l.allocations.reduce((s, a) => s + (a.discount ?? 0), 0)) <= round2(l.discount ?? 0)),
      { path: ['lines'], message: 'The discounts on the invoices are more than the discount on the voucher', ...always },
    )
    .refine((v) => v.lines.every((l) => !(l.discount > 0) || !!(l.partnerId ?? v.partnerId)), { path: ['lines'], message: 'A discount is given on a customer / supplier line', ...always });

export const cashVoucherSchema = cashRules(cashBase);
export type CashVoucherInput = z.infer<typeof cashVoucherSchema>;

export const updateJournalVoucherSchema = journalVoucherSchema.and(z.object({ version: z.number().int() }));
export const updateCashVoucherSchema = cashVoucherSchema.and(z.object({ version: z.number().int() }));

export interface VoucherLine {
  id: string;
  lineNo: number;
  account: AccountRef;
  partner: { id: string; code: string; name: string } | null;
  /**
   * How the line reads: a customer / supplier on its head's control account shows as its own account — the old
   * system's code (e.g. 10201200031 Home Style Furniture LLC), else its customer / supplier code.
   */
  ledger?: { code: string; name: string; head: string } | null;
  costCentreId: string | null;
  costCentre: string | null;
  description: string;
  debit: number;
  credit: number;
  allocations: { id: string; targetLineId: string; amount: number; discount?: number; target: OpenItem | null }[];
  /** Receipts / payments: the settlement discount given on this customer / supplier line … */
  discount?: number;
  /** … and the discount allowed / received line that books it (shown under its line, not edited on its own). */
  isDiscount?: boolean;
  /** Receipts: employee whose advance / loan this line repays, and which one. */
  employee?: { id: string; empNo: string; name: string } | null;
  loanId?: string | null;
  /** Payments: the advance / loan given on this line. */
  newLoan?: { kind: 'ADVANCE' | 'LOAN'; installment: number; startMonth: string; loanId: string } | null;
}

/** Receipt / payment side card: where the customer or supplier stands. */
export interface PartySummary {
  /** Balance on their control account(s): + they owe us (customer) / we owe them (supplier). */
  outstanding: number;
  openCount: number;
  openAmount: number;
  overdueAmount: number;
  /** Money received / paid in advance, not yet matched to an invoice / bill. */
  advance: number;
  pdcCount: number;
  pdcAmount: number;
  creditHold: string | null;
}

/** An employee advance / loan still being recovered. */
export interface EmployeeOpenLoan {
  loanId: string;
  number: string | null;
  kind: 'ADVANCE' | 'LOAN';
  docDate: string;
  amount: number;
  installment: number;
  balance: number;
  accountId: string;
  reason: string;
}

export interface VoucherDetail {
  id: string;
  docType: string;
  kind: VoucherKind | 'opening' | 'legacy';
  prefix: string;
  number: string | null;
  legacyNumber: string | null;
  docDate: string;
  status: import('./documents').DocStatus;
  narration: string;
  /** Formatted narration for screen and print; empty when the voucher has plain text only. */
  narrationRich?: string;
  reference: string;
  cashAccount: AccountRef | null;
  mode: PaymentMode | null;
  partner: { id: string; code: string; name: string } | null;
  chequeNo: string;
  chequeDate: string | null;
  drawerBank: string;
  bankAccountId: string | null;
  /** Settlement discount on the voucher (needs the discount approval). */
  discount: number;
  /** Recovery of a returned cheque. */
  replacesCheque: { id: string; chequeNo: string; amount: number; returnedOn: string | null } | null;
  totalDebit: number;
  totalCredit: number;
  lines: VoucherLine[];
  migrated: boolean;
  legacyFormType: string | null;
  legacyUser: string | null;
  reversalOf: { id: string; number: string | null } | null;
  reversedBy: { id: string; number: string | null } | null;
  createdBy: string | null;
  createdAt: string;
  submittedAt: string | null;
  approvedBy: string | null;
  /** Last approved change to the approved voucher (prints say REVISED). */
  revisedAt?: string | null;
  approvedAt: string | null;
  postedAt: string | null;
  /** Pending: why this user cannot approve it (a discount or an employee loan needs a special right), else null. */
  approveBlocker?: string | null;
  version: number;
  actions: import('./documents').WorkflowAction[];
}

/** Count and amount per status for a voucher list (the status tabs). */
export interface VoucherStatusSummary {
  status: import('./documents').DocStatus;
  count: number;
  amount: number;
}

export interface VoucherListItem {
  id: string;
  /** Something the approver must look at (a bill's VAT differs from its purchase order). */
  warning?: string | null;
  docType: string;
  number: string | null;
  docDate: string;
  status: import('./documents').DocStatus;
  narration: string;
  partyName: string | null;
  amount: number;
  migrated: boolean;
  createdBy: string | null;
  /** Who made / submitted it (approving your own needs the approve_own right). */
  createdById?: string;
  submittedById?: string | null;
  /** Voucher type prefix: JV, CPV, BPV, CRV, BRV, PDCR… */
  prefix: string;
  version: number;
  createdAt: string;
  /** Approval lists only: who last sent it back / approved / cancelled it, when, and the reason given. */
  actedBy?: string | null;
  actedAt?: string | null;
  actedNote?: string | null;
  /** The event behind it, e.g. reversal_of for an automatic reversal voucher. */
  actedAction?: string;
  /** When it was last submitted for approval (how long it has waited). */
  submittedAt?: string | null;
  /** How many times it has been sent back for rectification. */
  sentBackCount?: number;
  /** Approved by the same person who created it (allowed; shown for the audit trail). */
  selfApproved?: boolean;
}

export type VoucherStage = 'drafts' | 'pending' | 'rectification' | 'approved' | 'cancelled';
export const VOUCHER_STAGES = ['drafts', 'pending', 'rectification', 'approved', 'cancelled'] as const;

/** One line of the voucher audit log: who did what to which voucher, when. */
export interface VoucherAuditItem {
  id: string;
  at: string;
  action: string;
  actor: string | null;
  comment: string | null;
  fromStatus: string | null;
  toStatus: string | null;
  /** Approved by the person who created the voucher. */
  selfApproved: boolean;
  /** Changed after it was approved, before change approval was required. */
  changedAfterApproval: boolean;
  voucher: { id: string; number: string | null; docType: string; prefix: string; partyName: string | null; amount: number; status: string; migrated: boolean };
}

/** A voucher picked by "select all matching": what the bulk bar and the approve dialog need. */
export interface VoucherPick { warning?: string | null; id: string; version: number; number: string | null; docType: string; status: string; label: string | null; amount: number; createdById: string; submittedById?: string | null; createdBy: string | null; migrated: boolean }

/** One Voucher Manager card: how many, and how much — receipts and payments apart (adding them means nothing). */
export interface VoucherStageSummary {
  count: number;
  receipts: number;
  payments: number;
  other: number;
}

// ---------- Open items & allocation ----------

/**
 * How an open item is named to people: the invoice no. (customers); for suppliers the supplier's bill no. (new bills),
 * the goods receipt no. (old bills), the old bill reference (old journals) — the voucher no. only when there is none.
 */
export const openItemRef = (it: Pick<OpenItem, 'number' | 'invoiceNo' | 'supplierRef'> & { grns?: string[]; reference?: string | null }) =>
  it.invoiceNo || it.supplierRef || (it.grns?.length ? it.grns.join(', ') : '') || it.reference || it.number || '';

export interface OpenItem {
  lineId: string;
  documentId: string;
  number: string | null;
  docType: string;
  docDate: string;
  description: string;
  /** Original amount on the natural side (debit for customers, credit for suppliers). */
  amount: number;
  open: number;
  ageDays: number;
  /** Supplier bills: the supplier's own bill number and the goods receipts on it. */
  supplierRef?: string | null;
  grns?: string[];
  /** The voucher's reference (old purchases posted as journals carry the old bill no. here). */
  reference?: string | null;
  /** Customer invoices: the salesperson, the invoice number (R-…; old invoices were posted as LSV vouchers) and its sale order. */
  salesperson?: string | null;
  invoiceNo?: string | null;
  orderNo?: string | null;
  /** Settled so far: money received / paid, discounts, returns (credit notes, purchase returns), other adjustments. */
  received?: number;
  discounts?: number;
  returns?: number;
  adjustments?: number;
}

export const manualAllocationSchema = z.object({
  partnerId: z.string().uuid(),
  accountId: z.string().uuid(),
  debitLineId: z.string().uuid(),
  creditLineId: z.string().uuid(),
  amount: money().refine((n) => n > 0, 'Enter an amount'),
});
export type ManualAllocationInput = z.infer<typeof manualAllocationSchema>;

// ---------- Cheques ----------

export const CHEQUE_DIRECTIONS = ['RECEIVED', 'ISSUED'] as const;
export const CHEQUE_STATUSES = ['PENDING', 'DEPOSITED', 'CLEARED', 'RETURNED', 'CANCELLED', 'HANDED_BACK'] as const;
export type ChequeStatus = (typeof CHEQUE_STATUSES)[number];
export const CHEQUE_STATUS_LABELS: Record<ChequeStatus, string> = { PENDING: 'In hand', DEPOSITED: 'Deposited', CLEARED: 'Cleared', RETURNED: 'Bounced', CANCELLED: 'Cancelled', HANDED_BACK: 'Returned to customer' };

// ---------- Bounced cheques: recovery cases ----------

export const RECOVERY_STAGES = ['OPEN', 'LEGAL', 'CLOSED', 'WRITTEN_OFF'] as const;
export type RecoveryStage = (typeof RECOVERY_STAGES)[number];
export const RECOVERY_STAGE_LABELS: Record<RecoveryStage, string> = { OPEN: 'Recovering', LEGAL: 'Legal / collection', CLOSED: 'Recovered', WRITTEN_OFF: 'Written off' };

/** Bounce a received cheque (opens a recovery case) or hand back one not yet deposited (swap). */
export const bounceChequeSchema = z.object({
  date: isoDate,
  reason: z.string().trim().min(2, 'Say why').max(300),
  /** What the bank charged us for the returned cheque (posted to bank charges). */
  bankCharge: z.preprocess((v) => (v === '' || v == null ? 0 : v), money('Bank charge')).default(0),
  /** Optional: pass a charge on to the customer (added to what they owe). */
  customerCharge: z.preprocess((v) => (v === '' || v == null ? 0 : v), money('Charge to customer')).default(0),
  /** Cost centre for the charges, when their accounts need one. */
  costCentreId: uuidOpt,
  version: z.number().int(),
});
export type BounceChequeInput = z.infer<typeof bounceChequeSchema>;

export const depositChequeSchema = z.object({ date: isoDate, bankGlAccountId: z.string().uuid('Choose the bank'), version: z.number().int() });
export type DepositChequeInput = z.infer<typeof depositChequeSchema>;

export const RECOVERY_MODES = ['CASH', 'TRANSFER', 'CARD', 'ONLINE', 'CHEQUE', 'PDC'] as const;
/** One way the money comes back: cash, transfer, a new (post-dated) cheque… Several in one go. */
export const recoveryPaymentSchema = z
  .object({
    mode: z.enum(RECOVERY_MODES),
    amount: money().refine((n) => n > 0, 'Enter an amount'),
    cashAccountId: uuidOpt,
    chequeNo: z.string().trim().max(20).optional().default(''),
    chequeDate: z.union([z.literal(''), isoDate]).nullable().optional().transform((v) => v || null),
    drawerBank: opt(80),
  })
  .refine((p) => p.mode === 'PDC' || !!p.cashAccountId, { path: ['cashAccountId'], message: 'Choose the bank or cash account', ...always })
  .refine((p) => !['CHEQUE', 'PDC'].includes(p.mode) || (/^\d{1,20}$/.test(p.chequeNo) && !!p.chequeDate), { path: ['chequeNo'], message: 'Enter the cheque number and date', ...always });
export const recoverSchema = z.object({
  date: isoDate,
  note: opt(300),
  payments: z.array(recoveryPaymentSchema).max(20).default([]),
  /** Re-present the bounced cheque itself (same number) as a post-dated cheque. */
  redeposit: z.object({ date: isoDate, amount: money() }).nullable().optional(),
  /** Optional manual split over the re-opened invoices: invoice line → amount. Default: original invoices oldest first. */
  allocations: z.array(z.object({ targetLineId: z.string().uuid(), amount: money() })).max(500).optional(),
  version: z.number().int(),
}).refine((v) => v.payments.length > 0 || !!v.redeposit, { path: ['payments'], message: 'Add at least one payment', ...always });
export type RecoverInput = z.infer<typeof recoverSchema>;

export const recoveryLegalSchema = z.object({ date: isoDate, note: z.string().trim().min(2, 'Add a note').max(1000), version: z.number().int() });
export const recoveryWriteOffSchema = z.object({ date: isoDate, reason: z.string().trim().min(2, 'Say why').max(300), costCentreId: uuidOpt, version: z.number().int() });
export const recoveryNoteSchema = z.object({ note: z.string().trim().min(1).max(1000) });

export interface RecoveryCaseItem {
  id: string;
  number: string;
  kind: 'BOUNCE' | 'SWAP';
  stage: RecoveryStage;
  partner: { id: string; code: string; name: string };
  chequeNo: string;
  chequeDate: string;
  drawerBank: string;
  openedOn: string;
  reason: string;
  /** Cheque + charges passed on (+ replacement cheques that bounced again are re-collected inside the case). */
  due: number;
  /** Cash / transfers posted and replacement cheques cleared. */
  collected: number;
  /** Replacement post-dated cheques not cleared yet. */
  uncleared: number;
  writtenOff: number;
  outstanding: number;
  bounces: number;
  ageDays: number;
  lastActivity: string;
}

export interface RecoveryCaseDetail extends RecoveryCaseItem {
  chequeAmount: number;
  chargeAmount: number;
  legalOn: string | null;
  legalNote: string;
  closedOn: string | null;
  bounceDocument: { id: string; number: string | null };
  /** The invoices the cheque had paid and where they stand now. */
  invoices: { lineId: string; documentId: string; number: string | null; docDate: string; original: number; open: number }[];
  /** Customer debit lines raised by the case (bounce charges) still open. */
  charges: { lineId: string; number: string | null; amount: number; open: number }[];
  cheques: { id: string; chequeNo: string; chequeDate: string; amount: number; status: ChequeStatus; drawerBank: string; voucher: { id: string; number: string | null } | null }[];
  receipts: { id: string; number: string | null; docDate: string; mode: string | null; amount: number; status: string }[];
  events: { id: string; kind: string; amount: number; note: string; at: string; by: string | null; document: { id: string; number: string | null; docType: string } | null }[];
  version: number;
}

export interface RecoverySummary {
  open: number;
  legal: number;
  due: number;
  collected: number;
  uncleared: number;
  outstanding: number;
  dueSoon: { count: number; amount: number };
  repeatCustomers: number;
}

export const clearChequeSchema = z.object({
  date: isoDate,
  /** Bank account the received cheque was deposited into (issued cheques use their own bank). */
  bankGlAccountId: uuidOpt,
  version: z.number().int(),
});
export const returnChequeSchema = z.object({ date: isoDate, reason: req('Reason', 300), version: z.number().int() });

export interface ChequeItem {
  id: string;
  direction: (typeof CHEQUE_DIRECTIONS)[number];
  chequeNo: string;
  chequeDate: string;
  amount: number;
  status: ChequeStatus;
  partner: { id: string; code: string; name: string } | null;
  partyName: string;
  drawerBank: string;
  bankAccount: string | null;
  bankGlAccountId: string | null;
  voucher: { id: string; number: string | null } | null;
  clearVoucher: { id: string; number: string | null } | null;
  returnVoucher: { id: string; number: string | null } | null;
  clearedOn: string | null;
  returnedOn: string | null;
  depositedOn: string | null;
  depositAccountId: string | null;
  /** Bounced-cheque case this cheque opened, or belongs to as a replacement. */
  recoveryCase: { id: string; number: string } | null;
  memo: string;
  migrated: boolean;
  version: number;
}

// ---------- Bank reconciliation ----------

export const bankRecSchema = z.object({
  accountId: z.string().uuid('Select the bank account'),
  statementDate: isoDate,
  statementBalance: z.coerce.number({ error: 'Enter the statement balance' }).min(-1e11).max(1e11),
});
export const bankRecUpdateSchema = z.object({
  statementBalance: z.coerce.number({ error: 'Enter the statement balance' }).min(-1e11).max(1e11),
  clearedLineIds: z.array(z.string().uuid()).max(20000),
  version: z.number().int(),
});

export interface BankRecDetail {
  id: string;
  account: AccountRef;
  statementDate: string;
  statementBalance: number;
  status: 'DRAFT' | 'COMPLETED';
  /** Book balance as of the statement date. */
  bookBalance: number;
  /** Balance of lines cleared up to and including this reconciliation. */
  clearedBalance: number;
  difference: number;
  lines: { id: string; date: string; number: string | null; documentId: string; description: string; debit: number; credit: number; cleared: boolean; clearedElsewhere: boolean }[];
  version: number;
}

// ---------- Reports ----------

export interface TrialBalanceRow {
  accountId: string;
  code: string;
  name: string;
  level: number;
  type: AccountType;
  isPostable: boolean;
  openingDebit: number;
  openingCredit: number;
  debit: number;
  credit: number;
  closingDebit: number;
  closingCredit: number;
  /** Set on customer/supplier lines (trial balance by party): the partner under the control account. */
  partnerId?: string | null;
}

export interface LedgerRow {
  lineId: string;
  documentId: string;
  date: string;
  number: string | null;
  docType: string;
  description: string;
  partner: string | null;
  costCentre: string | null;
  debit: number;
  credit: number;
  balance: number;
  /** The account this line is on (shown when a ledger covers more than one account). */
  account?: { code: string; name: string } | null;
  /** The other side of the voucher: its largest other account, and how many more. */
  against?: { code: string; name: string; more: number; all: string[] } | null;
}

export interface LedgerReport {
  account: AccountRef | null;
  partner: { id: string; code: string; name: string } | null;
  from: string;
  to: string;
  opening: number;
  rows: LedgerRow[];
  totalDebit: number;
  totalCredit: number;
  closing: number;
}

/** Transaction summary: every posted voucher with its complete double entry and who created / approved it. */
export interface TransactionVoucher {
  documentId: string;
  number: string | null;
  legacyNumber: string | null;
  date: string;
  docType: string;
  voucherType: string | null;
  narration: string;
  reference: string;
  createdAt: string;
  createdBy: string;
  approvedAt: string | null;
  approvedBy: string | null;
  migrated: boolean;
  lines: { code: string; account: string; partner: string | null; costCentre: string | null; description: string; debit: number; credit: number }[];
}

export interface TransactionSummary {
  from: string;
  to: string;
  vouchers: TransactionVoucher[];
  /** Matching vouchers over all pages. */
  count: number;
  totalDebit: number;
  page: number;
  pageSize: number;
}

/** Accounting dashboard: everything on one call, live from the ledger. */
export interface AccountingDashboard {
  asOf: string;
  yearStart: string;
  /** From the ledger: gross / net margin this year, current ratio today, operating expenses this month. */
  ratios: { grossMarginPct: number | null; netMarginPct: number | null; currentRatio: number | null; currentAssets: number; currentLiabilities: number; cogsYtd: number; opexMonth: number };
  decisions: {
    pending: { count: number; amount: number };
    /** Pending + sent back per document type, so each opens its own list. */
    openByType: { docType: string; status: string; count: number }[];
    sentBack: { count: number; amount: number };
    drafts: number;
    oldPending: number;
    pdcReceivedDue: { count: number; amount: number };
    pdcIssuedDue: { count: number; amount: number };
    lastBankRec: string | null;
    lastClosedPeriod: string | null;
  };
  kpis: {
    revenueYtd: number;
    expensesYtd: number;
    profitYtd: number;
    cashAndBank: number;
    receivables: number;
    customerAdvances: number;
    payables: number;
    supplierAdvances: number;
    vatPayable: number;
  };
  /** Last 12 months, oldest first. */
  monthly: { month: string; revenue: number; expenses: number }[];
  ageing: { buckets: number[]; unapplied: number };
  expenseBreakdown: { code: string; name: string; amount: number }[];
  topCustomers: { partnerId: string; name: string; amount: number }[];
  cashAccounts: { accountId: string; code: string; name: string; kind: string; balance: number }[];
  costCentres: { code: string; name: string; revenue: number; expenses: number }[];
}

/**
 * Document types that change the ledger or stock. A month can only be closed when none dated in it
 * is still draft, pending, sent back or approved-but-unposted. Orders, requisitions, leave requests
 * and gate passes are ordinary open paperwork and do not hold a month open.
 */
export const PERIOD_BLOCKING_DOC_PREFIXES = ['acct.'] as const;
export const PERIOD_BLOCKING_DOC_TYPES = [
  'sales.invoice',
  'sales.credit_note',
  'sales.delivery',
  'inv.purchase_bill',
  'inv.purchase_return',
  'inv.goods_receipt',
  'inv.stock_issue',
  'inv.opening_stock',
  'mfg.fg_receipt',
  'hr.payroll_run',
  'hr.loan',
  'hr.final_settlement',
] as const;

/** Month-end close: one row per month with its checklist. */
export interface PeriodCloseMonth {
  periodId: string;
  name: string;
  month: string;
  startDate: string;
  endDate: string;
  status: 'OPEN' | 'CLOSED';
  version: number;
  closedAt: string | null;
  closedBy: string | null;
  /** Month has not ended yet. */
  current: boolean;
  checks: {
    openVouchers: number;
    /** Changes to approved vouchers of the month waiting for approval (they stop the lock too). */
    pendingChanges?: number;
    banks: { total: number; reconciled: number };
    pdcPending: number;
    payrollPosted: boolean;
    closingStockPosted: boolean;
  };
  revenue: number;
  expenses: number;
}

export interface StatementSection {
  key: string;
  label: string;
  /** Amount per cost-centre code plus TOTAL. */
  amounts: Record<string, number>;
  accounts: { id: string; code: string; name: string; amounts: Record<string, number> }[];
}

export interface ProfitLossReport {
  from: string;
  to: string;
  costCentres: { code: string; name: string }[];
  sections: StatementSection[];
  grossProfit: Record<string, number>;
  operatingProfit: Record<string, number>;
  netProfit: Record<string, number>;
  /** Periodic inventory: closing stock is posted by the month-end closing-stock JV (Phase 3). */
  note: string;
}

export interface BalanceSheetReport {
  asOf: string;
  sections: { key: string; label: string; total: number; groups: { code: string; name: string; total: number; accounts: { id?: string; code: string; name: string; amount: number }[] }[] }[];
  currentYearProfit: number;
  totalAssets: number;
  totalLiabilitiesEquity: number;
  difference: number;
}

export interface AgeingRow {
  partnerId: string;
  code: string;
  name: string;
  buckets: [number, number, number, number, number];
  unapplied: number;
  balance: number;
}
export const AGEING_BUCKETS = ['0–30', '31–60', '61–90', '91–180', 'Over 180'] as const;

export interface LegacyReconRow {
  code: string;
  name: string;
  partner: string | null;
  legacy: number;
  current: number;
  difference: number;
}

// ---------- Changes to approved vouchers (owner, 2026-10-26) ----------

export const CHANGE_KINDS = ['EDIT', 'CANCEL', 'MATCH', 'UNMATCH'] as const;
export type ChangeKind = (typeof CHANGE_KINDS)[number];
export const CHANGE_KIND_LABELS: Record<ChangeKind, string> = { EDIT: 'Change', CANCEL: 'Cancellation', MATCH: 'Matching to invoices', UNMATCH: 'Unmatching' };
export type ChangeStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'WITHDRAWN';

const changeReason = z.string().trim().min(5, 'Say why (at least 5 characters)').max(500);
/** A change to an approved voucher: its full new content (as on the voucher form) and why. */
export const changeEditSchema = z.object({ reason: changeReason, input: z.record(z.string(), z.unknown()) });
export const changeCancelSchema = z.object({ reason: changeReason });
export const changeDecisionSchema = z.object({ version: z.number().int(), note: z.string().trim().max(500).default('') });
export const changeMatchSchema = z.object({
  reason: changeReason,
  /** Everyone / one party oldest first, or the chosen pairs. */
  side: z.enum(['customers', 'suppliers']).optional(),
  partnerId: z.string().uuid().optional(),
  pairs: z.array(z.object({ debitLineId: z.string().uuid(), creditLineId: z.string().uuid(), amount: z.number().positive() })).max(5000).optional(),
});
export const changeUnmatchSchema = z.object({ reason: changeReason, allocationId: z.string().uuid() });

export interface ChangeRequestItem {
  id: string;
  kind: ChangeKind;
  status: ChangeStatus;
  reason: string;
  summary: string[];
  amount: number;
  requestedBy: string;
  requestedAt: string;
  decidedBy: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  decidedFor: string | null;
  selfApproved: boolean;
  version: number;
  /** The voucher (EDIT / CANCEL / UNMATCH) or the first of a matching batch. */
  voucher: { id: string; number: string | null; docType: string; prefix: string; partyName: string | null; amount: number; status: string } | null;
  vouchers: number;
  /** For the current user. */
  canDecide: boolean;
  canWithdraw: boolean;
}

export const voucherChangeSettingsSchema = z
  .object({
    requestRoleIds: z.array(z.string().uuid()).max(50),
    approveRoleIds: z.array(z.string().uuid()).max(50),
    standInUserId: z.string().uuid().nullable(),
    standInForUserId: z.string().uuid().nullable(),
    standInFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
    standInTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
    escalationDays: z.coerce.number().int().min(1, 'At least 1 day').max(30),
    version: z.number().int(),
  })
  .refine((v) => !v.standInUserId || (v.standInForUserId && v.standInFrom && v.standInTo), { path: ['standInTo'], message: 'Choose who is away and the dates' })
  .refine((v) => !v.standInFrom || !v.standInTo || v.standInTo >= v.standInFrom, { path: ['standInTo'], message: 'End date must be on or after the start' })
  .refine((v) => !v.standInUserId || v.standInUserId !== v.standInForUserId, { path: ['standInUserId'], message: 'Choose someone else as the stand-in' });
export type VoucherChangeSettingsInput = z.infer<typeof voucherChangeSettingsSchema>;

// ---------- One line per account (owner rule, 2026-10-26) ----------

/** A line as the merge rule sees it. `keep` lines (matched to invoices, holding a loan or a cheque) are never merged. */
export interface MergeableLine { accountId: string; partnerId?: string | null; employeeId?: string | null; costCentreId?: string | null; debit: number; credit: number; description?: string; keep?: boolean }
export interface LineMerge { account: string; lines: number[]; into: number | null }

/**
 * The same account (and the same cost centre, when there is one) appears once in a voucher: repeated lines are added
 * together, debits and credits netted, and a line that nets to zero is dropped. On customer, supplier and employee
 * accounts the person is part of the account (their own ledger code), so two different customers never merge.
 * Returns the merged lines (first position kept) and what was merged, for the note and the history.
 */
export function mergeEntryLines<T extends MergeableLine>(lines: T[], partyAccount: (accountId: string) => boolean): { lines: T[]; merges: LineMerge[] } {
  const groups = new Map<string, number[]>();
  lines.forEach((l, i) => {
    if (l.keep || !l.accountId) return;
    const key = [l.accountId, partyAccount(l.accountId) ? (l.partnerId ?? '') : '', l.employeeId ?? '', l.costCentreId ?? ''].join('|');
    groups.set(key, [...(groups.get(key) ?? []), i]);
  });
  const drop = new Set<number>();
  const replace = new Map<number, T | null>();
  const merges: LineMerge[] = [];
  for (const idx of groups.values()) {
    if (idx.length < 2) continue;
    const first = lines[idx[0]!]!;
    const net = Math.round(idx.reduce((s, i) => s + (lines[i]!.debit || 0) - (lines[i]!.credit || 0), 0) * 100) / 100;
    const desc = [...new Set(idx.map((i) => (lines[i]!.description ?? '').trim()).filter(Boolean))].join('; ').slice(0, 300);
    idx.slice(1).forEach((i) => drop.add(i));
    replace.set(idx[0]!, net === 0 ? null : { ...first, debit: net > 0 ? net : 0, credit: net < 0 ? -net : 0, description: desc });
    merges.push({ account: first.accountId, lines: idx.map((i) => i + 1), into: net === 0 ? null : idx[0]! + 1 });
  }
  if (!merges.length) return { lines, merges };
  const out: T[] = [];
  lines.forEach((l, i) => {
    if (drop.has(i)) return;
    if (replace.has(i)) { const r = replace.get(i); if (r) out.push(r); return; }
    out.push(l);
  });
  return { lines: out, merges };
}
